import { forwardRef, useEffect, useId, useLayoutEffect, useRef } from 'react';
import type { TtsModel, TtsParams, Voice } from '@shared/types';
import { VoicePicker } from '@/components/VoicePicker/VoicePicker';
import { TTS_MODEL_LABELS, TTS_MODELS } from '@/lib/modelLock';
import styles from './ParamPanel.module.css';

interface VoiceSelectionProps {
  /** Currently selected voiceId for the active block. */
  voiceId: string;
  /** Voices the active asset is allowed to use. Picker is hidden if length <= 1. */
  assetVoiceIds: string[];
  /** Project-wide voices for display-name lookup. */
  projectVoices: Voice[];
  /** Called when the user picks a different voice for the active block. */
  onVoiceChange(voiceId: string): void;
}

interface PanelProps {
  activeBlockIdx: number;
  params: TtsParams;
  onChange(next: TtsParams): void;
  /** Positioning: the DOM element of the active block and its containing
   *  column, used to compute marginTop + arrow top. Nulls disable positioning
   *  (first render before refs attach). */
  activeBlockEl: HTMLElement | null;
  blocksColEl: HTMLElement | null;
  /** When the block list changes structure (add/delete/swap), the panel
   *  skips the marginTop transition for a single paint. */
  skipTransitionToken: number;
  /** Generate-this-block CTA — primary action of the panel. Disabled when
   *  the block is locked, empty, currently generating, or `disabledReason`
   *  is truthy (e.g. no voice). */
  onGenerate?(): void;
  canGenerate?: boolean;
  isGenerating?: boolean;
  disabledReason?: string | null;
  /** Multi-voice (#173): when present and asset has >1 allowed voice, a
   *  VoicePicker is rendered above the Model selector so the user can
   *  choose which voice the next Generate uses for this block. */
  voiceSelection?: VoiceSelectionProps;
  /** Model-Lock (spec 2026-07-23): when set, the model select is replaced
   *  by a static label — the admin restricted this block's voice/asset to
   *  one model. */
  modelLock?: TtsModel | null;
}

const ARROW_MARGIN = 14;
// Arrow visual height (border-top 7 + border-bottom 7). The arrow's `top`
// is its top edge, so the lower bound = panelH - ARROW_MARGIN - ARROW_HEIGHT
// keeps the same visual gap at the bottom that ARROW_MARGIN gives at the top.
const ARROW_HEIGHT = 14;

export const ParamPanel = forwardRef<HTMLDivElement, PanelProps>(function ParamPanel(
  {
    activeBlockIdx,
    params,
    onChange,
    activeBlockEl,
    blocksColEl,
    skipTransitionToken,
    onGenerate,
    canGenerate = true,
    isGenerating = false,
    disabledReason,
    voiceSelection,
    modelLock,
  },
  forwardedRef,
) {
  const panelRef = useRef<HTMLDivElement | null>(null);
  const arrowRef = useRef<HTMLDivElement | null>(null);
  const lastSkipToken = useRef(skipTransitionToken);

  // Mirror of legacy positionParamPanel(idx). Recomputes whenever the active
  // block element, its column, the panel size (params change height), or the
  // skip-transition token changes.
  useLayoutEffect(() => {
    const panel = panelRef.current;
    const arrow = arrowRef.current;
    if (!panel || !arrow || !activeBlockEl || !blocksColEl) return;

    const skip = lastSkipToken.current !== skipTransitionToken;
    lastSkipToken.current = skipTransitionToken;

    if (skip) {
      panel.style.transition = 'none';
      panel.style.marginTop = '0px';
      // force reflow
      void panel.offsetHeight;
    }

    const panelH = panel.offsetHeight;
    const blockOffsetTop = activeBlockEl.offsetTop - blocksColEl.offsetTop;
    const blockH = activeBlockEl.offsetHeight;
    const currentMargin = parseFloat(panel.style.marginTop) || 0;

    const targetY = blockOffsetTop + ARROW_MARGIN;
    const relY = targetY - currentMargin;

    const arrowMaxTop = panelH - ARROW_MARGIN - ARROW_HEIGHT;
    if (relY >= ARROW_MARGIN && relY <= arrowMaxTop) {
      arrow.style.top = `${relY}px`;
    } else if (relY > arrowMaxTop) {
      const newMargin = blockOffsetTop + blockH - panelH;
      panel.style.marginTop = `${Math.max(0, newMargin)}px`;
      arrow.style.top = `${Math.max(
        ARROW_MARGIN,
        Math.min(targetY - Math.max(0, newMargin), arrowMaxTop),
      )}px`;
    } else {
      panel.style.marginTop = `${Math.max(0, blockOffsetTop)}px`;
      arrow.style.top = `${ARROW_MARGIN}px`;
    }

    if (skip) {
      void panel.offsetHeight;
      panel.style.transition = '';
    }
  }, [activeBlockEl, blocksColEl, params.model, skipTransitionToken]);

  return (
    <div ref={forwardedRef} className={styles.col}>
      <div ref={panelRef} className={styles.panel} data-param-panel="inner">
        <div ref={arrowRef} className={styles.arrow} />
        <ParamPanelBody
          activeBlockIdx={activeBlockIdx}
          params={params}
          onChange={onChange}
          onGenerate={onGenerate}
          canGenerate={canGenerate}
          isGenerating={isGenerating}
          disabledReason={disabledReason}
          voiceSelection={voiceSelection}
          modelLock={modelLock}
        />
      </div>
    </div>
  );
});

interface BodyProps {
  activeBlockIdx: number;
  params: TtsParams;
  onChange(next: TtsParams): void;
  onGenerate?(): void;
  canGenerate?: boolean;
  isGenerating?: boolean;
  disabledReason?: string | null;
  voiceSelection?: VoiceSelectionProps;
  /** See PanelProps.modelLock. */
  modelLock?: TtsModel | null;
}

/** The form-only contents of the ParamPanel — no `.col` wrapper, no arrow,
 *  no positioning. Reused by ParamPanel (desktop column) and by
 *  MobileParamSheet (slide-up bottom sheet at narrow viewports). */
export function ParamPanelBody({
  activeBlockIdx,
  params,
  onChange,
  onGenerate,
  canGenerate = true,
  isGenerating = false,
  disabledReason,
  voiceSelection,
  modelLock,
}: BodyProps) {
  // ParamPanelBody renders TWICE in the tree (desktop ParamPanel + the
  // CSS-hidden MobileParamSheet). All ids must be instance-unique (a shared
  // radio `name` once silently unchecked the visible copy, 2026-07-23).
  const uid = useId();
  const isV4 = params.model === 'v4';
  const showVoicePicker =
    !!voiceSelection && voiceSelection.assetVoiceIds.length > 1;
  return (
    <>
      <div className={styles.header}>Block {activeBlockIdx + 1}</div>

      {/* Voice (multi-voice assets only, #173) */}
      {showVoicePicker && voiceSelection && (
        <div className={styles.row}>
          <div className={styles.field}>
            <label htmlFor="ttsBlockVoice">Voice</label>
            <VoicePicker
              value={voiceSelection.voiceId}
              onChange={voiceSelection.onVoiceChange}
              assetVoiceIds={voiceSelection.assetVoiceIds}
              voices={voiceSelection.projectVoices}
              disabled={isGenerating}
            />
          </div>
        </div>
      )}

      {/* Model — locked to a static label when the admin restricted the
          voice/asset to one model (spec 2026-07-23-model-lock-design.md). */}
      <div className={styles.row}>
        <div className={styles.field}>
          <label htmlFor={`ttsModel-${uid}`}>Model</label>
          {modelLock ? (
            <div style={{ fontSize: 13, padding: '4px 0', color: 'var(--muted)' }}>
              {TTS_MODEL_LABELS[modelLock]}
            </div>
          ) : (
            <select
              id={`ttsModel-${uid}`}
              value={params.model}
              onChange={(e) =>
                onChange({ ...params, model: e.target.value as TtsModel })
              }
            >
              {TTS_MODELS.map((m) => (
                <option key={m} value={m}>{TTS_MODEL_LABELS[m]}</option>
              ))}
            </select>
          )}
        </div>
      </div>

      {/* v2 sliders */}
      {!isV4 && (
        <div className={styles.sliders}>
          <SliderItem
            label="Stability"
            endpoints={['More variable', 'More stable']}
            value={params.v2.stability}
            onChange={(v) => onChange({ ...params, v2: { ...params.v2, stability: v } })}
            warning={params.v2.stability < 0.3 ? 'Under 30% may lead to instability' : null}
          />
          <SliderItem
            label="Similarity"
            endpoints={['Low', 'High']}
            value={params.v2.similarity}
            onChange={(v) => onChange({ ...params, v2: { ...params.v2, similarity: v } })}
          />
          <SliderItem
            label="Style Exaggeration"
            endpoints={['None', 'Exaggerated']}
            value={params.v2.style}
            onChange={(v) => onChange({ ...params, v2: { ...params.v2, style: v } })}
            warning={params.v2.style > 0.5 ? 'Over 50% may lead to instability' : null}
          />
        </div>
      )}

      {/* v4 sliders — Eleven v4 has only Stability + Similarity (no Style,
          no Speed, no Speaker Boost) and no warning ranges. */}
      {isV4 && (
        <div className={styles.sliders}>
          <SliderItem
            label="Stability"
            endpoints={['Creative', 'Robust']}
            value={params.v4.stability}
            onChange={(v) => onChange({ ...params, v4: { ...params.v4, stability: v } })}
          />
          <SliderItem
            label="Similarity"
            endpoints={['Low', 'High']}
            value={params.v4.similarity}
            onChange={(v) => onChange({ ...params, v4: { ...params.v4, similarity: v } })}
          />
        </div>
      )}

      {/* Speaker Boost (v2 only) */}
      {!isV4 && (
        <div className={styles.speakerBoost}>
          <div className={styles.toggleRow}>
            <span className={styles.toggleLbl}>Speaker Boost</span>
            <label className={styles.toggle}>
              <input
                type="checkbox"
                checked={params.v2.speakerBoost}
                onChange={(e) =>
                  onChange({ ...params, v2: { ...params.v2, speakerBoost: e.target.checked } })
                }
              />
              <span className={styles.toggleSl} />
            </label>
          </div>
        </div>
      )}

      {onGenerate && (
        <button
          type="button"
          className={styles.generateBtn}
          disabled={isGenerating || !canGenerate || !!disabledReason}
          title={disabledReason ?? undefined}
          onClick={onGenerate}
          data-coach="generate-block"
        >
          {isGenerating ? (
            <>
              <i className={`ph-bold ph-circle-notch ${styles.spinIcon}`} />
              Generating…
            </>
          ) : (
            <>
              <i className="ph-fill ph-play" /> Generate Block {activeBlockIdx + 1}
            </>
          )}
        </button>
      )}
    </>
  );
}

interface SheetProps extends BodyProps {
  open: boolean;
  onClose(): void;
}

/** Slide-up bottom sheet for the ParamPanel — used at narrow viewports
 *  (<600px) where the right-column layout breaks down. ESC + backdrop tap
 *  close. Body content is the same `ParamPanelBody` as desktop. */
export function MobileParamSheet({ open, onClose, ...body }: SheetProps) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  return (
    <div
      className={`${styles.mobileSheet} ${open ? styles.mobileSheetOpen : ''}`}
      aria-hidden={!open}
    >
      <div
        className={styles.mobileSheetBackdrop}
        onClick={onClose}
        aria-hidden="true"
      />
      <div className={styles.mobileSheetPanel} role="dialog" aria-modal="true">
        <div className={styles.mobileSheetHandle} aria-hidden="true" />
        <div className={styles.mobileSheetClose}>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close parameters"
          >
            <i className="ph-bold ph-x" aria-hidden="true" />
          </button>
        </div>
        <div className={styles.mobileSheetContent}>
          <ParamPanelBody {...body} />
        </div>
      </div>
    </div>
  );
}

interface SliderItemProps {
  label: string;
  endpoints: [string, string];
  value: number; // 0..1
  onChange(v: number): void;
  warning?: string | null;
}

function SliderItem({ label, endpoints, value, onChange, warning }: SliderItemProps) {
  const pct = Math.round(value * 100);
  return (
    <div>
      <div className={styles.sliderHeader}>
        <span className={styles.sliderLbl}>{label}</span>
        <span className={styles.sliderVal}>{pct}%</span>
      </div>
      <div className={styles.sliderEndpoints}>
        <span>{endpoints[0]}</span>
        <span>{endpoints[1]}</span>
      </div>
      <input
        type="range"
        min={0}
        max={100}
        value={pct}
        className={styles.range}
        onChange={(e) => onChange(Number(e.target.value) / 100)}
      />
      {warning && <div className={styles.sliderWarning}>{warning}</div>}
    </div>
  );
}

